import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router';
import { Grid, Paper, Typography } from '@mui/material';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import b64toBlob from 'b64-to-blob';

import { useAnalyticContext } from '../../../../hooks/analyticContext';
import { configureStackedBarChartForCost } from '../../../ChartConfiguration/ChartConfiguration';
import ClaimsPerformance from '../../../../Models/Claims/ClaimsPreview.model';
import FilterOptions from '../FilterOptions';
import { getTotalCostGraph, exportAnalyticMembers, getLastClaimUpdatedOn } from '../AnalyticService';
import { ClaimAnalyticEnum } from '../../../../Enum/ClaimAnalyticEnum';
import { formatDate } from '../../../../utils/dateHelpers';

const TotalCost = () => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [graphSummary, setGraphSummary] = useState<ClaimsPerformance[]>([]);
    const [chartOptions, setChartOptions] = useState({});
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [lastUpdatedOn, setLastUpdatedOn] = useState('');
    const chartHeight = 380;

    const {
        tcHealthPlanIds,
        setTcHealthPlanIds,
        tcTeam,
        setTcTeam,
        showTcInActiveTeams,
        setShowTcInActiveTeams,
        tcYears,
        setTcYears,
        tcMonths,
        setTcMonths,
    } = useAnalyticContext();

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChartForCost(
                    'Total Cost',
                    '',
                    '',
                    'Cost',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [''],
                    'TotalCost',
                    history,
                    chartHeight
                )
            );
        }
        
        const data = await getLastClaimUpdatedOn();
        setLastUpdatedOn(data ? formatDate(new Date(`${data.split('T')[0]}T00:00:00`), 'MM/dd/yyyy') : '');
    }, [isFetched, setIsFetched, isGraphShow, history]);

    useEffect(() => {
        if (!isFetched && tcHealthPlanIds && tcHealthPlanIds.length > 0) {
            changeFilter();
            setIsFetched(true);
        }
        initialPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps;
    }, [tcHealthPlanIds]);

    const changeFilter = async () => {
        if (tcHealthPlanIds && tcHealthPlanIds.length > 0) {
            setIsGraphShow(false);
            const data = await getTotalCostGraph(
                tcHealthPlanIds,
                tcTeam,
                tcYears,
                tcMonths,
                showTcInActiveTeams
            );
            setGraphSummary(data);
            setIsPrepared(true);
            setIsGraphShow(true);
        }

        setChartOptions(
            configureStackedBarChartForCost(
                'Total Cost',
                '',
                '',
                'Cost',
                [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                [0],
                'TotalCost',
                history,
                chartHeight
            )
        );
    };

    useEffect(() => {
        if (graphSummary && isPrepared) {

            let xAxisValues = graphSummary.map(x => x.Category);

            let yAxisValues = [
                {
                    name: 'Total Cost',
                    data: graphSummary.map((x) => ({
                        y: x.TotalCost
                    })),
                    color: '#6495ED',
                    showInLegend: false
                }
            ];

            setChartOptions(
                configureStackedBarChartForCost(
                    'Total Cost',
                    '',
                    '',
                    'Cost',
                    yAxisValues,
                    xAxisValues,
                    'TotalCost',
                    history,
                    chartHeight
                )
            );

            setIsGraphShow(true);
            setIsPrepared(false);
        }
    }, [graphSummary, tcHealthPlanIds, isPrepared, history]);

    const handleDownload = async () => {
        try {
            setDownLoading(true);
            await exportDetail(); // your existing function
        } finally {
            setDownLoading(false);
        }
    };

    const exportDetail = async () => {
        const data = await exportAnalyticMembers(
            ClaimAnalyticEnum.TotalCost,
            tcHealthPlanIds,
            tcTeam,
            tcYears,
            tcMonths,
            showTcInActiveTeams
        );

        let typeName = 'TotalCost';
        let blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `${typeName} - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
    };

    return (
        <Grid size={{ xs: 12, lg: 12 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 480, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                    <FilterOptions
                        healthPlanIds={tcHealthPlanIds}
                        setHealthPlanIds={setTcHealthPlanIds}
                        years={tcYears}
                        setYears={setTcYears}
                        months={tcMonths}
                        setMonths={setTcMonths}
                        team={tcTeam}
                        setTeam={setTcTeam}
                        showInActiveTeams={showTcInActiveTeams}
                        setShowInActiveTeams={setShowTcInActiveTeams}
                        onFetch={changeFilter}
                        claimType={ClaimAnalyticEnum.TotalCost}
                    />
                    <Grid size={12} sx={{ position: 'relative' }}>
                        {!isGraphShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                            sx={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                zIndex: 1,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Last claim updated: {lastUpdatedOn || 'N/A'}
                            </Typography>

                            <Box
                                onClick={!downLoading ? handleDownload : undefined}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    cursor: downLoading ? 'default' : 'pointer',
                                }}
                            >
                                {downLoading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <FileDownloadIcon
                                        sx={{
                                            color: 'black',
                                            fontSize: 24,
                                        }}
                                        titleAccess="Export"
                                    />
                                )}
                            </Box>
                        </Box>
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default TotalCost;
